import { IconButton, WidgetSpinner } from '@/components/common'
import classNames from 'classnames'
import { useEffect, useRef } from 'react'

interface EditableTitleProps {
    value: string
    isEditing?: boolean
    onChange?: (val: string) => void
    onConfirm?: () => void
    onEdit?: () => void
    isLoading?: boolean
    errorText?: string
}

export const EditableTitle: React.FC<EditableTitleProps> = (props) => {
    const {
        value,
        errorText,
        isEditing,
        isLoading,
        onChange,
        onConfirm,
        onEdit,
    } = props

    const inputRef = useRef<HTMLInputElement>(null)
    const inputCloneRef = useRef<HTMLDivElement>(null)
    const resizeInput = () => {
        if (!inputRef.current || !inputCloneRef.current) {
            return
        }

        inputCloneRef.current.innerText = inputRef.current.value
        const width = inputCloneRef.current.offsetWidth
        inputRef.current.style.width = `${width.toString()}px`
    }

    useEffect(resizeInput, [])

    const inputClassNames = classNames(
        'outline-0 border-b text-heading-4 py-1 pr-7 whitespace-nowrap box-border',
        {
            'hover:bg-blue-50': !errorText,
            'bg-red-50 border-b-error': !!errorText,
            'bg-white border-b-neutral-300': !errorText && !isEditing,
            'bg-neutral-50 border-b-2 border-b-azure':
                !errorText && (isEditing || isLoading),
        }
    )

    const inputCloneClassname = classNames(
        inputClassNames,
        'absolute opacity-0 pointer-events-none'
    )

    const errorGridClassname = classNames(
        'grid transition-all duration-300 linear',
        {
            'grid-rows-[0fr]': !errorText,
            'grid-rows-[1fr]': !!errorText,
        }
    )

    return (
        <div className="space-y-1 px-6">
            <div className="flex">
                <div className="relative">
                    <input
                        data-testid="editable-title-input"
                        className={inputClassNames}
                        value={value}
                        onFocus={onEdit}
                        onBlur={onConfirm}
                        onChange={(event) => {
                            onChange?.(event.target.value)
                        }}
                        onInput={resizeInput}
                        disabled={isLoading}
                        ref={inputRef}
                    />
                    <div className={inputCloneClassname} ref={inputCloneRef} />
                    <div className="absolute right-0 top-1/2 -translate-y-1/2">
                        {!isLoading && !isEditing && (
                            <IconButton
                                icon="edit"
                                name={'editable-title-edit-button'}
                                size={'medium'}
                                onClick={() => {
                                    inputRef.current?.focus()
                                }}
                                className="block !text-azure"
                                variant="ghostPrimary"
                            />
                        )}
                        {!isLoading && isEditing && (
                            <IconButton
                                icon="check_circle_outline"
                                name={'editable-title-confirm-button'}
                                size={'medium'}
                                onClick={() => {
                                    inputRef.current?.blur()
                                }}
                                className="block !text-azure"
                                variant="ghostPrimary"
                            />
                        )}
                        {isLoading && <WidgetSpinner />}
                    </div>
                </div>
            </div>
            <div className={errorGridClassname}>
                <div className="overflow-hidden">
                    <div
                        data-testid="editable-title-error"
                        className="text-body-medium min-h-[1.5rem] font-normal text-error"
                    >
                        {errorText}
                    </div>
                </div>
            </div>
        </div>
    )
}
